// packages/frontend/src/pages/FileManagerPage.tsx
// 🎨 MODERN UI + ALL FEATURES RESTORED + UNZIP FEATURE ✅
import { useEffect, useState } from 'react'
import { useAuthStore } from '../stores/authStore'
import { useFileStore } from '../stores/fileStore'
import { useToastStore } from '../stores/toastStore'
import { FileItem } from '../components/FileItem'
import { UploadModal } from '../components/UploadModal'
import { CreateFolderModal } from '../components/CreateFolderModal'
import { CreateFileModal } from '../components/CreateFileModal'
import { RenameModal } from '../components/RenameModal'
import { MoveModal } from '../components/MoveModal'
import { BulkActionsBar } from '../components/BulkActionsBar'
import { DeleteConfirmationModal } from '../components/DeleteConfirmationModal'
import { FileEditorModal } from '../components/FileEditorModal'
import { FilePreviewModal } from '../components/FilePreviewModal'
import { StorageCounter } from '../components/StorageCounter'
import { KillSwitchModal } from '../components/KillSwitchModal'
import { AmpDomainModal } from '../components/AmpDomainModal'
import { TrashModal } from '../components/TrashModal'
import { CommandPalette } from '../components/CommandPalette'
import { FileTerminal } from '../components/FileTerminal'
import { CostWidget } from '../components/CostWidget'
import { RecentAndFavoritesSidebar } from '../components/RecentAndFavoritesSidebar'
import { InlineSearchInput } from '../components/InlineSearchInput'
import { UnzipModal } from '../components/UnzipModal'  // ✅ ADDED
import type { FileSystemItem } from '@shared'
import { 
  LogOut, 
  Upload, 
  Folder, 
  FileText,
  Grid, 
  List,
  Home,
  ChevronRight,
  ChevronLeft,
  Loader2,
  X,
  AlertOctagon,
  Plus,
  Zap,
  Globe,
  Trash2,
  Search,
  Terminal as TerminalIcon
} from 'lucide-react'

interface SearchResult {
  type: 'file' | 'folder';
  matchType: 'filename' | 'content' | 'foldername';
  relevance: number;
  context?: string;
  occurrences?: number;
  item: any;
}

export function FileManagerPage() {
  const { logout, role } = useAuthStore()
  const isTeam = role === 'team'

  const {
    currentPath,
    items,
    isLoading,
    selectedItems,
    viewMode,
    loadItems,
    navigateToFolder,
    toggleSelection,
    clearSelection,
    setViewMode,
    deleteItem,
    uploadFileWithProgress
  } = useFileStore()

  // 🎨 NEW: Drag & Drop State with Progress
  const [isDragging, setIsDragging] = useState(false)
  const [dragCounter, setDragCounter] = useState(0)
  const [uploadingFiles, setUploadingFiles] = useState<Map<string, number>>(new Map())

  // Search state
  const [isSearchMode, setIsSearchMode] = useState(false)
  const [searchResults, setSearchResults] = useState<SearchResult[]>([])
  const [searchQuery, setSearchQuery] = useState('')

  // 🎨 NEW: Command Palette State
  const [showCommandPalette, setShowCommandPalette] = useState(false)

  // CLI terminal panel (Ctrl+`) — shell-style commands over the same store.
  const [showTerminal, setShowTerminal] = useState(false)

  // Modal states
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [showFolderModal, setShowFolderModal] = useState(false)
  const [showFileModal, setShowFileModal] = useState(false)
  const [showRenameModal, setShowRenameModal] = useState(false)
  const [showMoveModal, setShowMoveModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showEditorModal, setShowEditorModal] = useState(false)
  const [showPreviewModal, setShowPreviewModal] = useState(false)
  const [showKillSwitchModal, setShowKillSwitchModal] = useState(false)
  const [showAmpDomainModal, setShowAmpDomainModal] = useState(false)
  const [showTrashModal, setShowTrashModal] = useState(false)
  // Search bar toggle — collapsed by default (just a button in the toolbar),
  // expands into full AdvancedSearch UI when clicked. Keyboard: Ctrl/Cmd+F to open.
  const [showSearchBar, setShowSearchBar] = useState(false)
  const [showUnzipModal, setShowUnzipModal] = useState(false)  // ✅ ADDED
  
  // Modal data
  const [renameItem, setRenameItem] = useState<FileSystemItem | null>(null)
  const [moveItems, setMoveItems] = useState<FileSystemItem[]>([])
  const [deleteItems, setDeleteItems] = useState<FileSystemItem[]>([])
  const [editItem, setEditItem] = useState<FileSystemItem | null>(null)
  const [previewItem, setPreviewItem] = useState<FileSystemItem | null>(null)
  const [unzipItem, setUnzipItem] = useState<FileSystemItem | null>(null)  // ✅ ADDED
  const [isDeleting, setIsDeleting] = useState(false)

  // Load items on mount. Team's root is scoped server-side to their allowed
  // folders (amp/ + image/), so both roles just load the root here.
  useEffect(() => {
    useToastStore.getState().clearToasts()
    loadItems()
  }, [loadItems])

  // 🎨 FIXED: Drag & Drop with Chunked Upload Support
  useEffect(() => {
    const handleDragEnter = (e: DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
      setDragCounter(prev => prev + 1)
      if (e.dataTransfer?.items && e.dataTransfer.items.length > 0) {
        setIsDragging(true)
      }
    }

    const handleDragLeave = (e: DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
      setDragCounter(prev => prev - 1)
      if (dragCounter - 1 === 0) {
        setIsDragging(false)
      }
    }

    const handleDragOver = (e: DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
    }

    const handleDrop = async (e: DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
      setIsDragging(false)
      setDragCounter(0)

      const files = e.dataTransfer?.files
      if (!files || files.length === 0) return

      // Upload each file with progress tracking
      for (let i = 0; i < files.length; i++) {
        const file = files[i]
        const fileId = `${file.name}-${Date.now()}-${i}`
        
        try {
          setUploadingFiles(prev => new Map(prev).set(fileId, 0))

          // Use uploadFileWithProgress (supports chunking for files >50MB)
          await uploadFileWithProgress(
            file,
            false, // Default to private
            (loaded, total) => {
              const percentage = Math.round((loaded / total) * 100)
              setUploadingFiles(prev => new Map(prev).set(fileId, percentage))
            }
          )

          setUploadingFiles(prev => {
            const newMap = new Map(prev)
            newMap.delete(fileId)
            return newMap
          })

          useToastStore.getState().addToast({
            type: 'success',
            message: `✓ ${file.name} uploaded (${file.size > 50 * 1024 * 1024 ? 'chunked' : 'direct'})`
          })
        } catch (error) {
          setUploadingFiles(prev => {
            const newMap = new Map(prev)
            newMap.delete(fileId)
            return newMap
          })

          useToastStore.getState().addToast({
            type: 'error',
            message: `Failed to upload ${file.name}`
          })
        }
      }
    }

    window.addEventListener('dragenter', handleDragEnter)
    window.addEventListener('dragleave', handleDragLeave)
    window.addEventListener('dragover', handleDragOver)
    window.addEventListener('drop', handleDrop)

    return () => {
      window.removeEventListener('dragenter', handleDragEnter)
      window.removeEventListener('dragleave', handleDragLeave)
      window.removeEventListener('dragover', handleDragOver)
      window.removeEventListener('drop', handleDrop)
    }
  }, [dragCounter, uploadFileWithProgress])

  // Clipboard paste → upload. Enables screenshot workflow: Snipping Tool /
  // Print Screen → Ctrl+V into the app → uploaded as `pasted-<timestamp>.png`.
  // Only triggers when the user isn't typing (skip if focus is on an input).
  useEffect(() => {
    const handlePaste = async (e: ClipboardEvent) => {
      const target = e.target as HTMLElement | null
      const tag = target?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || target?.isContentEditable) return

      const items = e.clipboardData?.items
      if (!items) return

      const filesToUpload: File[] = []
      for (const item of items) {
        if (item.kind !== 'file') continue
        const file = item.getAsFile()
        if (!file) continue
        // Clipboard images arrive as `image.png` (browsers) or empty name — rename
        // with timestamp so multiple pastes don't collide server-side.
        const ext = file.name.split('.').pop() || (file.type.split('/')[1] ?? 'bin')
        const stamped = new File(
          [file],
          `pasted-${Date.now()}.${ext}`,
          { type: file.type }
        )
        filesToUpload.push(stamped)
      }
      if (filesToUpload.length === 0) return

      e.preventDefault()

      for (let i = 0; i < filesToUpload.length; i++) {
        const file = filesToUpload[i]
        const fileId = `${file.name}-${Date.now()}-${i}`
        try {
          setUploadingFiles(prev => new Map(prev).set(fileId, 0))
          await uploadFileWithProgress(file, false, (loaded, total) => {
            const percentage = Math.round((loaded / total) * 100)
            setUploadingFiles(prev => new Map(prev).set(fileId, percentage))
          })
          setUploadingFiles(prev => {
            const m = new Map(prev); m.delete(fileId); return m
          })
          useToastStore.getState().addToast({
            type: 'success',
            message: `✓ ${file.name} pasted (${(file.size / 1024).toFixed(0)} KB)`
          })
        } catch {
          setUploadingFiles(prev => {
            const m = new Map(prev); m.delete(fileId); return m
          })
          useToastStore.getState().addToast({
            type: 'error',
            message: `Failed to upload pasted content`
          })
        }
      }
    }

    window.addEventListener('paste', handlePaste)
    return () => window.removeEventListener('paste', handlePaste)
  }, [uploadFileWithProgress])

  // 🎨 NEW: Command Palette Hotkey
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Command Palette: Ctrl+K or Cmd+K
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault()
        setShowCommandPalette(true)
        return
      }

      // Terminal panel: Ctrl+` toggles the CLI dock.
      if ((e.ctrlKey || e.metaKey) && e.key === '`') {
        e.preventDefault()
        setShowTerminal(v => !v)
        return
      }

      // Escape: Close terminal / command palette or clear selection
      if (e.key === 'Escape') {
        if (showTerminal) {
          setShowTerminal(false)
          return
        }
        if (showCommandPalette) {
          setShowCommandPalette(false)
          return
        }
        useToastStore.getState().clearToasts()
        clearSelection()
        if (isSearchMode) {
          handleClearSearch()
        }
        return
      }

      // Don't trigger shortcuts when modals are open
      if (showUploadModal || showFolderModal || showFileModal || showRenameModal ||
          showMoveModal || showDeleteModal || showEditorModal || showPreviewModal ||
          showKillSwitchModal || showAmpDomainModal || showTrashModal || showCommandPalette ||
          showUnzipModal || showTerminal) return
      
      // Select All: Ctrl+A
      if ((e.ctrlKey || e.metaKey) && e.key === 'a') {
        e.preventDefault()
        const currentItems = isSearchMode ? convertSearchResultsToFileSystemItems(searchResults) : items
        const allIds = currentItems.map(item => item.item.id)
        allIds.forEach(id => toggleSelection(id))
        return
      }
      
      // Rename: F2
      if (e.key === 'F2' && selectedItems.size === 1) {
        e.preventDefault()
        const currentItems = isSearchMode ? convertSearchResultsToFileSystemItems(searchResults) : items
        const selectedId = Array.from(selectedItems)[0]
        const selectedItem = currentItems.find(item => item.item.id === selectedId)
        if (selectedItem) {
          handleRename(selectedItem)
        }
        return
      }
      
      // Delete: Delete key
      if (e.key === 'Delete' && selectedItems.size > 0) {
        e.preventDefault()
        const currentItems = isSearchMode ? convertSearchResultsToFileSystemItems(searchResults) : items
        const selectedItemsData = currentItems.filter(item => selectedItems.has(item.item.id))
        handleDeleteItems(selectedItemsData)
        return
      }

      // Up one folder: Alt+ArrowUp (Windows/Linux) or Cmd+ArrowUp (macOS)
      if ((e.altKey || e.metaKey) && e.key === 'ArrowUp') {
        e.preventDefault()
        handleBackToParent()
        return
      }

      // Toggle search: Ctrl+F / Cmd+F (intercepts browser Find, matches file
      // managers' conventions). Escape while open collapses it.
      if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
        e.preventDefault()
        setShowSearchBar(v => !v)
        return
      }
      if (e.key === 'Escape' && showSearchBar) {
        e.preventDefault()
        setShowSearchBar(false)
        return
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [showCommandPalette, showUploadModal, showFolderModal, showFileModal, showRenameModal,
      showMoveModal, showDeleteModal, showEditorModal, showPreviewModal, showKillSwitchModal,
      showAmpDomainModal, showTrashModal, showUnzipModal, showTerminal, showSearchBar, clearSelection, items, searchResults, selectedItems, toggleSelection, isSearchMode])

  const refreshStorageCounter = () => {
    if ((window as any).refreshStorageUsage) {
      (window as any).refreshStorageUsage();
    }
  };

  const handleLogout = async () => {
    await logout()
  }

  const handleNavigateToFolder = async (path: string) => {
    setIsSearchMode(false)
    setSearchResults([])
    setSearchQuery('')
    await navigateToFolder(path)
    clearSelection()
  }

  const handleBackToRoot = async () => {
    setIsSearchMode(false)
    setSearchResults([])
    setSearchQuery('')
    // Home is the root for both roles — team's root is scoped server-side to
    // their allowed folders (amp/ + image/).
    await navigateToFolder('')
    clearSelection()
  }

  // Navigate one level up. Falls back to Home when already at the top. At the
  // root there's nowhere to go — team's root already shows only their folders.
  const handleBackToParent = async () => {
    setIsSearchMode(false)
    setSearchResults([])
    setSearchQuery('')

    if (!currentPath) return

    const parts = currentPath.split('/').filter(Boolean)
    parts.pop()
    await navigateToFolder(parts.join('/'))
    clearSelection()
  }

  // 🎨 RESTORED: Search handlers
  const handleSearchResults = (results: SearchResult[], query: string) => {
    setSearchResults(results)
    setSearchQuery(query)
    setIsSearchMode(true)
    clearSelection()
  }

  const handleClearSearch = () => {
    setIsSearchMode(false)
    setSearchResults([])
    setSearchQuery('')
    clearSelection()
  }

  const convertSearchResultsToFileSystemItems = (results: SearchResult[]): FileSystemItem[] => {
    return results.map(result => ({
      type: result.type,
      item: result.item
    }))
  }

  const handleRename = (item: FileSystemItem) => {
    setRenameItem(item)
    setShowRenameModal(true)
  }

  const handleBulkMove = (items: any[]) => {
    setMoveItems(items)
    setShowMoveModal(true)
  }

  const handleEdit = (item: FileSystemItem) => {
    if (item.type === 'file') {
      setEditItem(item)
      setShowEditorModal(true)
    }
  }

  const handleView = (item: FileSystemItem) => {
    if (item.type === 'file') {
      setPreviewItem(item)
      setShowPreviewModal(true)
    } else if (item.type === 'folder') {
      handleNavigateToFolder((item.item as any).path)
    }
  }

  const handlePreviewEdit = () => {
    if (previewItem) {
      setShowPreviewModal(false)
      setEditItem(previewItem)
      setShowEditorModal(true)
    }
  }

  // ✅ ADDED: Unzip handlers
  const handleOpenUnzipModal = (item: FileSystemItem) => {
    setUnzipItem(item)
    setShowUnzipModal(true)
  }

  const handleUnzipSuccess = () => {
    loadItems() // Reload folder to show extracted files
    refreshStorageCounter() // Update storage usage
    clearSelection()
  }

  const handleFileCreated = () => {
    loadItems();
    refreshStorageCounter();
  }

  const handleDeleteItems = (itemsToDelete: FileSystemItem[]) => {
    setDeleteItems(itemsToDelete)
    setShowDeleteModal(true)
  }

  const handleSingleDelete = (item: FileSystemItem) => {
    setDeleteItems([item])
    setShowDeleteModal(true)
  }

  const handleConfirmDelete = async () => {
    if (deleteItems.length === 0) return

    setIsDeleting(true)

    try {
      if (deleteItems.length === 1) {
        await deleteItem(deleteItems[0].item.id, deleteItems[0].type)
        useToastStore.getState().addToast({
          type: 'success',
          message: `${deleteItems[0].type === 'file' ? 'File' : 'Folder'} deleted successfully`,
        })
      } else {
        const itemsToDelete = deleteItems.map(item => ({
          id: item.item.id,
          type: item.type as 'file' | 'folder'
        }))
        await useFileStore.getState().bulkDelete(itemsToDelete)
      }

      await loadItems()
      refreshStorageCounter()
      clearSelection()
      setShowDeleteModal(false)
      setDeleteItems([])
    } catch (error) {
      console.error('Delete error:', error)
    } finally {
      setIsDeleting(false)
    }
  }

  const handleEditorSave = () => {
    loadItems()
    refreshStorageCounter()
  }

  const handleKillSwitchRefresh = () => {
    loadItems()
  }

  const getBreadcrumbs = () => {
    if (!currentPath) return []
    return currentPath.split('/').filter(Boolean)
  }

  const getPageTitle = () => {
    if (isSearchMode) return `Search results for "${searchQuery}"`
    if (!currentPath) return 'All Files'
    return currentPath.split('/').pop() || 'All Files'
  }

  const getPageSubtitle = () => {
    if (isSearchMode) {
      const fileCount = searchResults.filter(r => r.type === 'file').length
      const folderCount = searchResults.filter(r => r.type === 'folder').length
      const parts = []
      if (fileCount > 0) parts.push(`${fileCount} file${fileCount !== 1 ? 's' : ''}`)
      if (folderCount > 0) parts.push(`${folderCount} folder${folderCount !== 1 ? 's' : ''}`)
      return parts.join(' and ')
    }
    const fileCount = items.filter(item => item.type === 'file').length
    const folderCount = items.filter(item => item.type === 'folder').length
    const parts = []
    if (fileCount > 0) parts.push(`${fileCount} file${fileCount !== 1 ? 's' : ''}`)
    if (folderCount > 0) parts.push(`${folderCount} folder${folderCount !== 1 ? 's' : ''}`)
    return parts.join(' and ')
  }

  const currentItems = isSearchMode 
    ? convertSearchResultsToFileSystemItems(searchResults) 
    : items

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* 🎨 NEW: Drag & Drop Overlay with Upload Progress */}
      {isDragging && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
          <div className="flex flex-col items-center gap-4 rounded-2xl border border-primary-500/50 bg-primary-500/10 p-12">
            <Upload className="h-16 w-16 text-primary-400 animate-bounce" />
            <div className="text-center">
              <p className="text-2xl font-semibold text-white">Drop files to upload</p>
              <p className="text-dark-400 mt-2">
                Files will be uploaded to {currentPath || 'root'}
              </p>
              <p className="text-xs text-dark-500 mt-1">
                ⚡ Large files (&gt;50MB) use chunked upload automatically
              </p>
            </div>
          </div>
        </div>
      )}

      {/* 🎨 NEW: Upload Progress Indicator */}
      {uploadingFiles.size > 0 && (
        <div className="fixed bottom-6 right-6 z-40 space-y-2">
          {Array.from(uploadingFiles.entries()).map(([fileId, progress]) => (
            <div
              key={fileId}
              className="glass-strong rounded-xl p-4 w-80 animate-slide-up"
            >
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-white truncate">
                  {fileId.split('-')[0]}
                </p>
                <p className="text-xs text-primary-400">{progress}%</p>
              </div>
              <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-primary-500 to-primary-600 transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 🎨 NEW: Modern Navbar with Glass Morphism */}
      <nav className="sticky top-0 z-40 border-b border-white/5 bg-black/40 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-screen-2xl items-center justify-between px-6">
          {/* Left: Logo */}
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-primary-500 to-primary-600">
              <Zap className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">Vendetta</h1>
              <p className="text-xs text-dark-400">CDN Platform</p>
            </div>
          </div>

          {/* Right: Actions */}
          <div className="flex items-center gap-2">
            {/* Real-time cost widget — admin only, self-hidden for team */}
            <CostWidget />

            {/* Admin-only controls — hidden from team role */}
            {!isTeam && (
              <>
                <button
                  onClick={() => setShowAmpDomainModal(true)}
                  className="flex items-center gap-2 rounded-lg border border-purple-500/30 bg-purple-500/10 px-3 py-2 text-sm font-medium text-purple-400 transition-all hover:bg-purple-500/20 hover:border-purple-500/50"
                  title="Rotate AMP subdomain"
                >
                  <Globe className="h-4 w-4" />
                  <span className="hidden sm:inline">AMP Domain</span>
                </button>

                <button
                  onClick={() => setShowKillSwitchModal(true)}
                  className="flex items-center gap-2 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm font-medium text-red-400 transition-all hover:bg-red-500/20 hover:border-red-500/50"
                >
                  <AlertOctagon className="h-4 w-4" />
                  <span className="hidden sm:inline">Kill Switch</span>
                </button>
              </>
            )}

            {/* Trash — available for admin and team (team sees own scope only) */}
            <button
              onClick={() => setShowTrashModal(true)}
              className="flex items-center gap-2 rounded-lg border border-white/5 bg-white/5 px-3 py-2 text-sm font-medium text-white transition-all hover:bg-white/10"
              title="Recycle bin"
            >
              <Trash2 className="h-4 w-4" />
              <span className="hidden sm:inline">Trash</span>
            </button>

            {/* Terminal / CLI — admin and team; commands respect the same scope */}
            <button
              onClick={() => setShowTerminal(v => !v)}
              className="flex items-center gap-2 rounded-lg border border-white/5 bg-white/5 px-3 py-2 text-sm font-medium text-white transition-all hover:bg-white/10"
              title="Terminal (Ctrl+`)"
            >
              <TerminalIcon className="h-4 w-4" />
              <span className="hidden sm:inline">Terminal</span>
            </button>

            <button
              onClick={handleLogout}
              className="flex items-center gap-2 rounded-lg border border-white/5 bg-white/5 px-3 py-2 text-sm font-medium text-white transition-all hover:bg-white/10"
            >
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </nav>

      <div className="mx-auto max-w-screen-2xl p-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Sidebar — favorites + storage. Search moved out to a top bar in
              the main content so it gets full width and doesn't compete with
              filters for narrow sidebar space. */}
          <div className="lg:col-span-3">
            <div className="lg:sticky lg:top-24 space-y-4">
              <RecentAndFavoritesSidebar
                currentPath={currentPath}
                onNavigate={handleNavigateToFolder}
                refreshKey={items.length}
              />

              {!isTeam && <StorageCounter />}
            </div>
          </div>

          {/* Main Content - RESPONSIVE */}
          <main className="lg:col-span-9">
            <div className="space-y-6">
              {/* 🎨 NEW: Modern Breadcrumb with clickable segments + Up button */}
              {currentPath && !isSearchMode && (() => {
                const crumbs = getBreadcrumbs()
                // Base path prefix used to reconstruct full path per segment.
                // Both roles now browse from the real root, so no prefix.
                const basePrefix = ''
                return (
                  <div className="flex flex-wrap items-center gap-2 text-sm">
                    <button
                      onClick={handleBackToParent}
                      className="flex items-center gap-1 rounded-lg border border-white/5 bg-white/5 px-2 py-1 text-dark-300 transition-colors hover:bg-white/10 hover:text-white"
                      title="Up one level"
                    >
                      <ChevronLeft className="h-4 w-4" />
                      <span>Up</span>
                    </button>
                    <button
                      onClick={handleBackToRoot}
                      className="flex items-center gap-1 rounded-lg px-2 py-1 text-dark-400 transition-colors hover:bg-white/5 hover:text-white"
                    >
                      <Home className="h-4 w-4" />
                      <span>Home</span>
                    </button>
                    {crumbs.map((segment, index) => {
                      // Each crumb navigates to its exact path (cumulative join).
                      const target = basePrefix + crumbs.slice(0, index + 1).join('/')
                      const isLast = index === crumbs.length - 1
                      return (
                        <div key={index} className="flex items-center gap-2">
                          <ChevronRight className="h-4 w-4 text-dark-600" />
                          {isLast ? (
                            <span className="text-white font-medium">{segment}</span>
                          ) : (
                            <button
                              onClick={() => handleNavigateToFolder(target)}
                              className="text-dark-300 hover:text-white hover:underline"
                            >
                              {segment}
                            </button>
                          )}
                        </div>
                      )
                    })}
                  </div>
                )
              })()}

              {/* Search Mode Indicator */}
              {isSearchMode && (
                <div className="flex items-center justify-between rounded-xl border border-primary-500/30 bg-primary-500/10 px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-500/20">
                      <AlertOctagon className="h-4 w-4 text-primary-400" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-white">Search Mode Active</p>
                      <p className="text-xs text-dark-400">
                        Showing results for "{searchQuery}"
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={handleClearSearch}
                    className="rounded-lg p-2 text-primary-400 hover:bg-primary-500/20"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              )}

              {/* 🎨 NEW: Header with Actions */}
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-3xl font-bold text-white">{getPageTitle()}</h2>
                  <p className="text-dark-400 mt-1">{getPageSubtitle()}</p>
                </div>

                {/* 🎨 NEW: Floating Action Buttons */}
                <div className="flex items-center gap-2">
                  {selectedItems.size > 0 && (
                    <div className="flex items-center gap-2 rounded-xl border border-primary-500/30 bg-primary-500/10 px-4 py-2">
                      <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary-500 text-xs font-bold text-white">
                        {selectedItems.size}
                      </div>
                      <span className="text-sm font-medium text-primary-400">selected</span>
                      <button
                        onClick={clearSelection}
                        className="ml-2 rounded-lg p-1 text-primary-400 hover:bg-primary-500/20"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  )}

                  {/* Search — inline in toolbar. Collapsed = icon only.
                      Expanded = compact input replaces the icon in-place, so
                      buttons stay in the same row. Filter panel (if user opens
                      it) still expands from within AdvancedSearch itself. */}
                  {showSearchBar ? (
                    <InlineSearchInput
                      currentPath={currentPath}
                      onResults={handleSearchResults}
                      onClear={handleClearSearch}
                      onClose={() => setShowSearchBar(false)}
                    />
                  ) : (
                    <button
                      onClick={() => setShowSearchBar(true)}
                      className={`rounded-lg p-2 transition-all ${
                        isSearchMode
                          ? 'bg-primary-500 text-white'
                          : 'text-dark-400 hover:bg-white/5 hover:text-white'
                      }`}
                      title="Search (Ctrl+F)"
                    >
                      <Search className="h-4 w-4" />
                    </button>
                  )}

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setViewMode('grid')}
                      className={`rounded-lg p-2 transition-all ${
                        viewMode === 'grid'
                          ? 'bg-primary-500 text-white'
                          : 'text-dark-400 hover:bg-white/5 hover:text-white'
                      }`}
                    >
                      <Grid className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => setViewMode('list')}
                      className={`rounded-lg p-2 transition-all ${
                        viewMode === 'list'
                          ? 'bg-primary-500 text-white'
                          : 'text-dark-400 hover:bg-white/5 hover:text-white'
                      }`}
                    >
                      <List className="h-4 w-4" />
                    </button>
                  </div>

                  <div className="h-6 w-px bg-white/10" />

                  <button
                    onClick={() => setShowUploadModal(true)}
                    className="flex items-center gap-2 rounded-xl bg-primary-500 px-4 py-2 text-sm font-semibold text-white transition-all hover:bg-primary-600 hover:shadow-lg hover:shadow-primary-500/50"
                  >
                    <Upload className="h-4 w-4" />
                    Upload
                  </button>

                  <button
                    onClick={() => setShowCommandPalette(true)}
                    className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-4 py-2 text-sm font-medium text-white transition-all hover:bg-white/10"
                  >
                    <Plus className="h-4 w-4" />
                    New
                  </button>
                </div>
              </div>

              {/* Loading State */}
              {isLoading && !isSearchMode && (
                <div className="flex h-96 flex-col items-center justify-center">
                  <Loader2 className="mb-4 h-8 w-8 animate-spin text-primary-500" />
                  <p className="text-dark-400">Loading files...</p>
                </div>
              )}

              {/* 🎨 NEW: Modern File Grid - FULLY RESPONSIVE */}
              {!isLoading && currentItems.length > 0 && (
                <div className={
                  viewMode === 'grid'
                    ? 'grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4'
                    : 'space-y-2'
                }>
                  {currentItems.map((item, index) => (
                    <FileItem
                      key={`${item.item.id}-${index}`}
                      item={item}
                      viewMode={viewMode}
                      isSelected={selectedItems.has(item.item.id)}
                      onSelect={toggleSelection}
                      onNavigate={handleNavigateToFolder}
                      onRename={handleRename}
                      onDelete={handleSingleDelete}
                      onView={handleView}
                      onEdit={handleEdit}
                      onUnzip={handleOpenUnzipModal}
                    />
                  ))}
                </div>
              )}

              {/* Empty State */}
              {!isLoading && currentItems.length === 0 && (
                <div className="flex h-96 flex-col items-center justify-center rounded-2xl border-2 border-dashed border-white/10">
                  <div className="mb-4 rounded-2xl bg-white/5 p-6">
                    {isSearchMode ? (
                      <FileText className="h-12 w-12 text-dark-400" />
                    ) : (
                      <Folder className="h-12 w-12 text-dark-400" />
                    )}
                  </div>
                  <h3 className="mb-2 text-xl font-semibold text-white">
                    {isSearchMode ? 'No results found' : 'No files yet'}
                  </h3>
                  <p className="mb-6 text-dark-400">
                    {isSearchMode 
                      ? `No files match your search criteria`
                      : 'Drag & drop files here or click upload to get started'
                    }
                  </p>
                  {!isSearchMode && (
                    <div className="flex gap-3">
                      <button 
                        onClick={() => setShowUploadModal(true)}
                        className="flex items-center gap-2 rounded-xl bg-primary-500 px-6 py-3 font-semibold text-white transition-all hover:bg-primary-600"
                      >
                        <Upload className="h-4 w-4" />
                        Upload Files
                      </button>
                      <button 
                        onClick={() => setShowFileModal(true)}
                        className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-6 py-3 font-semibold text-white transition-all hover:bg-white/10"
                      >
                        <FileText className="h-4 w-4" />
                        Create File
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </main>
        </div>
      </div>

      {/* Modals */}
      <UploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        currentPath={currentPath}
      />
      
      <CreateFolderModal
        isOpen={showFolderModal}
        onClose={() => setShowFolderModal(false)}
        currentPath={currentPath}
      />

      <CreateFileModal
        isOpen={showFileModal}
        onClose={() => setShowFileModal(false)}
        currentPath={currentPath}
        onFileCreated={handleFileCreated}
      />

      <RenameModal
        isOpen={showRenameModal}
        onClose={() => {
          setShowRenameModal(false)
          setRenameItem(null)
        }}
        item={renameItem}
      />

      <MoveModal
        isOpen={showMoveModal}
        onClose={() => {
          setShowMoveModal(false)
          setMoveItems([])
        }}
        items={moveItems}
      />

      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false)
          setDeleteItems([])
        }}
        onConfirm={handleConfirmDelete}
        items={deleteItems.map(item => ({ 
          name: item.item.name, 
          type: item.type as 'file' | 'folder' 
        }))}
        isLoading={isDeleting}
      />

      <FileEditorModal
        isOpen={showEditorModal}
        onClose={() => {
          setShowEditorModal(false)
          setEditItem(null)
        }}
        fileId={editItem?.item.id || ''}
        fileName={editItem?.item.name || ''}
        onSave={handleEditorSave}
      />

      <FilePreviewModal
        isOpen={showPreviewModal}
        onClose={() => {
          setShowPreviewModal(false)
          setPreviewItem(null)
        }}
        fileId={previewItem?.item.id || ''}
        fileName={previewItem?.item.name || ''}
        filePath={(previewItem?.item as any)?.path || ''}
        mimeType={(previewItem?.item as any)?.mimeType || ''}
        fileSize={(previewItem?.item as any)?.size || 0}
        isPublic={(previewItem?.item as any)?.isPublic || false}
        isEditable={(previewItem?.item as any)?.isEditable || false}
        onEdit={handlePreviewEdit}
      />

      <KillSwitchModal
        isOpen={showKillSwitchModal}
        onClose={() => setShowKillSwitchModal(false)}
        selectedFileIds={Array.from(selectedItems)}
        onRefresh={handleKillSwitchRefresh}
      />

      <AmpDomainModal
        isOpen={showAmpDomainModal}
        onClose={() => setShowAmpDomainModal(false)}
      />

      <TrashModal
        isOpen={showTrashModal}
        onClose={() => setShowTrashModal(false)}
        onChange={() => loadItems(currentPath)}
      />

      {/* ✅ ADDED: UnzipModal */}
      <UnzipModal
        isOpen={showUnzipModal}
        onClose={() => {
          setShowUnzipModal(false)
          setUnzipItem(null)
        }}
        file={unzipItem?.item}
        onSuccess={handleUnzipSuccess}
      />

      <CommandPalette
        isOpen={showCommandPalette}
        onClose={() => setShowCommandPalette(false)}
        onCreateFolder={() => { setShowCommandPalette(false); setShowFolderModal(true) }}
        onCreateFile={() => { setShowCommandPalette(false); setShowFileModal(true) }}
      />

      <FileTerminal isOpen={showTerminal} onClose={() => setShowTerminal(false)} />

      <BulkActionsBar
        selectedItems={selectedItems}
        items={currentItems}
        onClearSelection={clearSelection}
        onMove={handleBulkMove}
      />
    </div>
  )
}